import React, { useState, useEffect } from 'react';
import { 
    Settings, Palette, Globe, Video, Image, Upload,
    Save, Loader2, Check, Code, AlertTriangle, X, Calendar
} from 'lucide-react';
import { apiFetch } from '../utils/api';

/**
 * ClubSettings - Club customization panel for Club Admins
 */
const ClubSettings = ({ user, onClubUpdated }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [activeTab, setActiveTab] = useState('branding');
    
    // Club data
    const [club, setClub] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        primary_color: '#2c3e50',
        language: 'fi',
        logo_url: '',
        hero_video_url: ''
    });
    
    // Scripts
    const [scripts, setScripts] = useState([]);
    const [showScriptForm, setShowScriptForm] = useState(false);
    
    // Season config
    const [seasonConfig, setSeasonConfig] = useState({
        season_start_month: 8,
        season_start_day: 1,
        season_end_month: 6,
        season_end_day: 30
    });
    const [showMigrationConfirm, setShowMigrationConfirm] = useState(false);
    const [migrationImpact, setMigrationImpact] = useState(null);
    const [savingSeasons, setSavingSeasons] = useState(false);

    useEffect(() => {
        fetchClubData();
        fetchSeasonConfig();
    }, []);

    const fetchClubData = async () => {
        try {
            // Get current user's club
            const res = await apiFetch('/api/club/current');
            if (res.ok) {
                const data = await res.json();
                setClub(data);
                setFormData({
                    name: data.name || '',
                    description: data.description || '',
                    primary_color: data.primary_color || '#2c3e50',
                    language: data.language || 'fi',
                    logo_url: data.logo_url || '',
                    hero_video_url: data.hero_video_url || ''
                });
            }
            
            // Get scripts
            const scriptsRes = await apiFetch('/api/club/scripts');
            if (scriptsRes.ok) {
                const scriptsData = await scriptsRes.json();
                setScripts(scriptsData);
            }
        } catch (err) {
            console.error('Club data fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSeasonConfig = async () => {
        try {
            const res = await apiFetch('/api/seasons/config');
            if (res.ok) {
                const data = await res.json();
                setSeasonConfig(data);
            }
        } catch (err) {
            console.error('Season config fetch error:', err);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaved(false);
        
        try {
            const res = await apiFetch('/api/club/update', {
                method: 'PUT',
                body: JSON.stringify(formData)
            });
            
            if (res.ok) {
                const updated = await res.json();
                setClub(updated);
                if (onClubUpdated) {
                    onClubUpdated(updated);
                }
                setSaved(true);
                setTimeout(() => setSaved(false), 3000);
            }
        } catch (err) {
            console.error('Club update error:', err);
        } finally {
            setSaving(false);
        }
    };

    const addScript = async (script) => {
        try {
            const res = await apiFetch('/api/club/scripts', {
                method: 'POST',
                body: JSON.stringify(script)
            });
            
            if (res.ok) {
                fetchClubData();
                setShowScriptForm(false);
            }
        } catch (err) {
            console.error('Script add error:', err);
        }
    };

    const deleteScript = async (scriptId) => {
        if (!window.confirm('Haluatko varmasti poistaa tämän skriptin?')) return;
        
        try {
            await apiFetch(`/api/club/scripts/${scriptId}`, { method: 'DELETE' });
            fetchClubData();
        } catch (err) {
            console.error('Script delete error:', err);
        }
    };

    const saveSeasonConfig = async (confirmMigration = false) => {
        setSavingSeasons(true);
        
        try {
            const res = await apiFetch('/api/seasons/config', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...seasonConfig,
                    confirm_migration: confirmMigration
                })
            });
            
            const data = await res.json();
            
            if (data.requires_confirmation) {
                // Show migration confirmation dialog
                setMigrationImpact(data.impact);
                setShowMigrationConfirm(true);
            } else {
                // Success
                setShowMigrationConfirm(false);
                setMigrationImpact(null);
                setSaved(true);
                setTimeout(() => setSaved(false), 3000);
            }
        } catch (err) {
            console.error('Season config save error:', err);
        } finally {
            setSavingSeasons(false);
        }
    };

    const confirmMigration = async () => {
        await saveSeasonConfig(true);
    };

    if (loading) {
        return (
            <div className="club-settings-loading">
                <Loader2 size={32} className="spin" />
                <span>Ladataan asetuksia...</span>
            </div>
        );
    }

    return (
        <div className="club-settings">
            <div className="settings-header">
                <h2><Settings size={24} /> Klubin asetukset</h2>
                <button 
                    className="save-btn"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? (
                        <><Loader2 size={16} className="spin" /> Tallennetaan...</>
                    ) : saved ? (
                        <><Check size={16} /> Tallennettu!</>
                    ) : (
                        <><Save size={16} /> Tallenna muutokset</>
                    )}
                </button>
            </div>

            {/* Tabs */}
            <div className="settings-tabs">
                <button 
                    className={`tab ${activeTab === 'branding' ? 'active' : ''}`}
                    onClick={() => setActiveTab('branding')}
                >
                    <Palette size={16} /> Ulkoasu
                </button>
                <button 
                    className={`tab ${activeTab === 'localization' ? 'active' : ''}`}
                    onClick={() => setActiveTab('localization')}
                >
                    <Globe size={16} /> Kieli
                </button>
                <button 
                    className={`tab ${activeTab === 'media' ? 'active' : ''}`}
                    onClick={() => setActiveTab('media')}
                >
                    <Video size={16} /> Media
                </button>
                <button 
                    className={`tab ${activeTab === 'scripts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('scripts')}
                >
                    <Code size={16} /> Skriptit
                </button>
                <button 
                    className={`tab ${activeTab === 'seasons' ? 'active' : ''}`}
                    onClick={() => setActiveTab('seasons')}
                >
                    <Calendar size={16} /> Kausi
                </button>
            </div>

            {/* Content */}
            <div className="settings-content">
                {activeTab === 'branding' && (
                    <div className="tab-content">
                        <h3><Palette size={18} /> Brändäys</h3>
                        
                        <div className="form-group">
                            <label>Klubin nimi</label>
                            <input 
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                placeholder="esim. Turun Noppanikkari"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Kuvaus</label>
                            <textarea 
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Lyhyt kuvaus klubista..."
                                rows={3}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Pääväri</label>
                            <div className="color-picker">
                                <input 
                                    type="color"
                                    value={formData.primary_color}
                                    onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                                />
                                <input 
                                    type="text"
                                    value={formData.primary_color}
                                    onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                                    placeholder="#8b5cf6"
                                />
                                <div 
                                    className="color-preview"
                                    style={{ background: formData.primary_color }}
                                />
                            </div>
                        </div>
                        
                        <div className="color-presets">
                            <span>Esimerkkivärit:</span>
                            {['#8b5cf6', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#ec4899'].map(color => (
                                <button
                                    key={color}
                                    className="color-preset"
                                    style={{ background: color }}
                                    onClick={() => setFormData({ ...formData, primary_color: color })}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'localization' && (
                    <div className="tab-content">
                        <h3><Globe size={18} /> Kieli ja alue</h3>
                        
                        <div className="form-group">
                            <label>Käyttöliittymän kieli</label>
                            <select 
                                value={formData.language}
                                onChange={e => setFormData({ ...formData, language: e.target.value })}
                            >
                                <option value="fi">Suomi</option>
                                <option value="en">English</option>
                                <option value="sv">Svenska</option>
                            </select>
                        </div>
                        
                        <div className="info-box">
                            <Globe size={16} />
                            <span>
                                Kielen vaihtaminen vaikuttaa käyttöliittymän teksteihin.
                                Käyttäjien syöttämä sisältö pysyy alkuperäisellä kielellä.
                            </span>
                        </div>
                    </div>
                )}

                {activeTab === 'media' && (
                    <div className="tab-content">
                        <h3><Video size={18} /> Kuvat ja videot</h3>
                        
                        <div className="form-group">
                            <label><Image size={14} /> Logo URL</label>
                            <input 
                                type="url"
                                value={formData.logo_url}
                                onChange={e => setFormData({ ...formData, logo_url: e.target.value })}
                                placeholder="https://example.com/logo.png"
                            />
                            {formData.logo_url && (
                                <div className="preview-box">
                                    <img src={formData.logo_url} alt="Logo preview" />
                                </div>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label><Video size={14} /> Hero Video URL</label>
                            <input 
                                type="url"
                                value={formData.hero_video_url}
                                onChange={e => setFormData({ ...formData, hero_video_url: e.target.value })}
                                placeholder="https://example.com/video.mp4"
                            />
                            <p className="help-text">
                                Video näytetään etusivun taustalla. Suositeltu koko: 1920x1080, MP4-muoto.
                            </p>
                        </div>
                    </div>
                )}

                {activeTab === 'scripts' && (
                    <div className="tab-content">
                        <h3><Code size={18} /> Mukautetut skriptit</h3>
                        
                        <div className="warning-box">
                            <AlertTriangle size={16} />
                            <span>
                                <strong>Varoitus:</strong> Skriptit suoritetaan sellaisenaan.
                                Lisää vain luotettavista lähteistä peräisin olevia skriptejä.
                            </span>
                        </div>
                        
                        <button 
                            className="add-script-btn"
                            onClick={() => setShowScriptForm(true)}
                        >
                            <Code size={16} /> Lisää skripti
                        </button>
                        
                        {scripts.length > 0 && (
                            <div className="scripts-list">
                                {scripts.map(script => (
                                    <div key={script.id} className="script-item">
                                        <div className="script-info">
                                            <span className="script-position">{script.position}</span>
                                            <span className="script-desc">{script.description || 'Ei kuvausta'}</span>
                                        </div>
                                        <button 
                                            className="delete-btn"
                                            onClick={() => deleteScript(script.id)}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'seasons' && (
                    <div className="tab-content">
                        <h3><Calendar size={18} /> Kausiväli</h3>
                        
                        <div className="info-box" style={{ marginBottom: '1.5rem' }}>
                            <Calendar size={16} />
                            <span>
                                Määrittele milloin kausi alkaa ja loppuu. Oletus on 1.8. - 30.6.
                                Sessiot ryhmitellään automaattisesti kausiin näiden päivämäärien mukaan.
                            </span>
                        </div>
                        
                        <div className="season-dates">
                            <div className="date-group">
                                <label>Kauden alku</label>
                                <div className="date-inputs">
                                    <input 
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={seasonConfig.season_start_day}
                                        onChange={e => setSeasonConfig({
                                            ...seasonConfig,
                                            season_start_day: parseInt(e.target.value) || 1
                                        })}
                                        placeholder="Päivä"
                                        style={{ width: '80px' }}
                                    />
                                    <span>.</span>
                                    <select 
                                        value={seasonConfig.season_start_month}
                                        onChange={e => setSeasonConfig({
                                            ...seasonConfig,
                                            season_start_month: parseInt(e.target.value)
                                        })}
                                    >
                                        {[
                                            { v: 1, l: 'Tammikuu' }, { v: 2, l: 'Helmikuu' }, { v: 3, l: 'Maaliskuu' },
                                            { v: 4, l: 'Huhtikuu' }, { v: 5, l: 'Toukokuu' }, { v: 6, l: 'Kesäkuu' },
                                            { v: 7, l: 'Heinäkuu' }, { v: 8, l: 'Elokuu' }, { v: 9, l: 'Syyskuu' },
                                            { v: 10, l: 'Lokakuu' }, { v: 11, l: 'Marraskuu' }, { v: 12, l: 'Joulukuu' }
                                        ].map(m => (
                                            <option key={m.v} value={m.v}>{m.l}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="date-group">
                                <label>Kauden loppu</label>
                                <div className="date-inputs">
                                    <input 
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={seasonConfig.season_end_day}
                                        onChange={e => setSeasonConfig({
                                            ...seasonConfig,
                                            season_end_day: parseInt(e.target.value) || 30
                                        })}
                                        placeholder="Päivä"
                                        style={{ width: '80px' }}
                                    />
                                    <span>.</span>
                                    <select 
                                        value={seasonConfig.season_end_month}
                                        onChange={e => setSeasonConfig({
                                            ...seasonConfig,
                                            season_end_month: parseInt(e.target.value)
                                        })}
                                    >
                                        {[
                                            { v: 1, l: 'Tammikuu' }, { v: 2, l: 'Helmikuu' }, { v: 3, l: 'Maaliskuu' },
                                            { v: 4, l: 'Huhtikuu' }, { v: 5, l: 'Toukokuu' }, { v: 6, l: 'Kesäkuu' },
                                            { v: 7, l: 'Heinäkuu' }, { v: 8, l: 'Elokuu' }, { v: 9, l: 'Syyskuu' },
                                            { v: 10, l: 'Lokakuu' }, { v: 11, l: 'Marraskuu' }, { v: 12, l: 'Joulukuu' }
                                        ].map(m => (
                                            <option key={m.v} value={m.v}>{m.l}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        
                        <div className="season-preview" style={{ 
                            marginTop: '1.5rem', 
                            padding: '1rem', 
                            background: 'rgba(139, 92, 246, 0.1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(139, 92, 246, 0.3)'
                        }}>
                            <strong style={{ color: '#a78bfa' }}>Esikatselu:</strong>
                            <p style={{ margin: '0.5rem 0 0', color: '#c9d1d9' }}>
                                Kausi: {seasonConfig.season_start_day}.{seasonConfig.season_start_month}. 
                                - {seasonConfig.season_end_day}.{seasonConfig.season_end_month}.
                                {seasonConfig.season_end_month < seasonConfig.season_start_month && (
                                    <span style={{ opacity: 0.7 }}> (seuraavana vuonna)</span>
                                )}
                            </p>
                        </div>
                        
                        <button 
                            className="btn-primary"
                            onClick={() => saveSeasonConfig(false)}
                            disabled={savingSeasons}
                            style={{ marginTop: '1.5rem' }}
                        >
                            {savingSeasons ? (
                                <><Loader2 size={16} className="spin" /> Tallennetaan...</>
                            ) : (
                                <><Save size={16} /> Tallenna kausiväli</>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Add Script Modal */}
            {showScriptForm && (
                <AddScriptModal 
                    onClose={() => setShowScriptForm(false)}
                    onAdd={addScript}
                />
            )}

            {/* Migration Confirmation Modal */}
            {showMigrationConfirm && migrationImpact && (
                <div className="modal-overlay" onClick={() => setShowMigrationConfirm(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><AlertTriangle size={20} style={{ color: '#f59e0b' }} /> Vahvista migraatio</h3>
                            <button className="modal-close" onClick={() => setShowMigrationConfirm(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p style={{ color: '#c9d1d9', marginBottom: '1rem' }}>
                                Sinulla on <strong>{migrationImpact.totalSessions}</strong> sessiota, 
                                joista <strong style={{ color: '#f59e0b' }}>{migrationImpact.affectedSessions}</strong> siirtyy 
                                eri kauteen uusilla asetuksilla.
                            </p>
                            
                            {Object.keys(migrationImpact.seasonChanges || {}).length > 0 && (
                                <div style={{ 
                                    background: 'rgba(0,0,0,0.3)', 
                                    padding: '1rem', 
                                    borderRadius: '8px',
                                    marginBottom: '1rem' 
                                }}>
                                    <strong style={{ color: '#8b949e', fontSize: '0.85rem' }}>Muutokset:</strong>
                                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem', color: '#c9d1d9' }}>
                                        {Object.entries(migrationImpact.seasonChanges).map(([change, count]) => (
                                            <li key={change}>{change}: <strong>{count}</strong> sessiota</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            <div className="warning-box" style={{ marginBottom: 0 }}>
                                <AlertTriangle size={16} />
                                <span>
                                    <strong>Mitä tapahtuu:</strong> Uudet kaudet luodaan automaattisesti ja 
                                    sessiot siirretään niihin. Klubin eniten käytetty pisteytysstrategia 
                                    valitaan kaikille kausille ja tulokset lasketaan uudelleen.
                                </span>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={() => setShowMigrationConfirm(false)}
                            >
                                Peruuta
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={confirmMigration}
                                disabled={savingSeasons}
                                style={{ background: '#f59e0b' }}
                            >
                                {savingSeasons ? (
                                    <><Loader2 size={16} className="spin" /> Suoritetaan...</>
                                ) : (
                                    'Vahvista migraatio'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .club-settings {
                    padding: 1.5rem;
                    max-width: 800px;
                    margin: 0 auto;
                }

                .club-settings-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    padding: 3rem;
                    color: #8b949e;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .settings-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .settings-header h2 {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin: 0;
                }

                .save-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.25rem;
                    background: #238636;
                    border: none;
                    border-radius: 8px;
                    color: white;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .save-btn:hover {
                    background: #2ea043;
                }

                .save-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .settings-tabs {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #30363d;
                    padding-bottom: 0.5rem;
                    flex-wrap: wrap;
                }

                .tab {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    background: transparent;
                    border: none;
                    color: #8b949e;
                    cursor: pointer;
                    border-radius: 8px 8px 0 0;
                    transition: all 0.2s;
                }

                .tab:hover {
                    background: rgba(0, 0, 0, 0.08);
                    color: #1a1a1a;
                }

                .tab.active {
                    background: rgba(139, 92, 246, 0.1);
                    color: #a78bfa;
                    border-bottom: 2px solid #8b5cf6;
                }

                .settings-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 12px;
                    padding: 1.5rem;
                }

                .tab-content h3 {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #f0f6fc;
                    margin: 0 0 1.5rem 0;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px solid #30363d;
                }

                .form-group {
                    margin-bottom: 1.25rem;
                }

                .form-group label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 0.5rem;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 0.75rem;
                    background: #0d1117;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #8b5cf6;
                }

                .color-picker {
                    display: flex;
                    gap: 0.75rem;
                    align-items: center;
                }

                .color-picker input[type="color"] {
                    width: 50px;
                    height: 40px;
                    padding: 0;
                    border: none;
                    cursor: pointer;
                }

                .color-picker input[type="text"] {
                    flex: 1;
                }

                .color-preview {
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    border: 2px solid #30363d;
                }

                .color-presets {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 0.75rem;
                    color: #8b949e;
                    font-size: 0.85rem;
                }

                .color-preset {
                    width: 28px;
                    height: 28px;
                    border-radius: 6px;
                    border: 2px solid transparent;
                    cursor: pointer;
                    transition: transform 0.2s;
                }

                .color-preset:hover {
                    transform: scale(1.1);
                }

                .info-box {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    padding: 1rem;
                    background: rgba(59, 130, 246, 0.1);
                    border: 1px solid rgba(59, 130, 246, 0.3);
                    border-radius: 8px;
                    color: #60a5fa;
                    font-size: 0.9rem;
                    line-height: 1.5;
                }

                .warning-box {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    padding: 1rem;
                    background: rgba(245, 158, 11, 0.1);
                    border: 1px solid rgba(245, 158, 11, 0.3);
                    border-radius: 8px;
                    color: #f59e0b;
                    font-size: 0.9rem;
                    line-height: 1.5;
                    margin-bottom: 1.5rem;
                }

                .preview-box {
                    margin-top: 1rem;
                    padding: 1rem;
                    background: rgba(0, 0, 0, 0.3);
                    border-radius: 8px;
                    text-align: center;
                }

                .preview-box img {
                    max-width: 200px;
                    max-height: 100px;
                    object-fit: contain;
                }

                .help-text {
                    color: #6b7280;
                    font-size: 0.8rem;
                    margin-top: 0.5rem;
                }

                .add-script-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    background: #21262d;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    cursor: pointer;
                    margin-bottom: 1rem;
                }

                .add-script-btn:hover {
                    background: #30363d;
                }

                .scripts-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .script-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem 1rem;
                    background: rgba(0, 0, 0, 0.2);
                    border: 1px solid #30363d;
                    border-radius: 8px;
                }

                .script-position {
                    background: rgba(139, 92, 246, 0.2);
                    color: #a78bfa;
                    padding: 0.2rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    margin-right: 0.75rem;
                }

                .script-desc {
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .delete-btn {
                    background: transparent;
                    border: none;
                    color: #ef4444;
                    cursor: pointer;
                    padding: 0.25rem;
                }

                /* Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                    padding: 1rem;
                }

                .modal-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    max-width: 500px;
                    width: 100%;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.25rem;
                    border-bottom: 1px solid #30363d;
                }

                .modal-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                }

                .modal-close {
                    background: transparent;
                    border: none;
                    color: #8b949e;
                    cursor: pointer;
                }

                .modal-body {
                    padding: 1.25rem;
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding: 1.25rem;
                    border-top: 1px solid #30363d;
                }

                .btn-primary {
                    padding: 0.75rem 1.25rem;
                    background: #8b5cf6;
                    border: none;
                    border-radius: 8px;
                    color: white;
                    font-weight: 600;
                    cursor: pointer;
                }

                .btn-secondary {
                    padding: 0.75rem 1.25rem;
                    background: #21262d;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    cursor: pointer;
                }

                .season-dates {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                }

                @media (max-width: 600px) {
                    .season-dates {
                        grid-template-columns: 1fr;
                    }
                }

                .date-group label {
                    display: block;
                    margin-bottom: 0.5rem;
                    color: #c9d1d9;
                    font-weight: 500;
                }

                .date-inputs {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .date-inputs span {
                    color: #8b949e;
                    font-size: 1.1rem;
                }

                .date-inputs input,
                .date-inputs select {
                    padding: 0.75rem;
                    background: #0d1117;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .date-inputs select {
                    flex: 1;
                }

                .btn-primary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .btn-primary:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
            `}</style>
        </div>
    );
};

// Add Script Modal
const AddScriptModal = ({ onClose, onAdd }) => {
    const [position, setPosition] = useState('head_end');
    const [description, setDescription] = useState('');
    const [script, setScript] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!script.trim()) return;
        onAdd({ position, description, script_content: script });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Lisää skripti</h3>
                    <button className="modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>Sijainti</label>
                            <select value={position} onChange={e => setPosition(e.target.value)}>
                                <option value="head_start">Head (alussa)</option>
                                <option value="head_end">Head (lopussa)</option>
                                <option value="body_start">Body (alussa)</option>
                                <option value="body_end">Body (lopussa)</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Kuvaus</label>
                            <input 
                                type="text"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="esim. Google Analytics"
                            />
                        </div>
                        <div className="form-group">
                            <label>Skripti</label>
                            <textarea 
                                value={script}
                                onChange={e => setScript(e.target.value)}
                                placeholder="<script>...</script>"
                                rows={6}
                                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                            />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            Peruuta
                        </button>
                        <button type="submit" className="btn-primary">
                            Lisää
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClubSettings;

